// Transport for the WRITE half of a shared "tasks assigned to X" link: the
// anonymous page never touches AppSync (every table is userPool-authed), so a
// visitor's edit, completion, photo upload or photo removal goes to the
// share-tasks-write Lambda's public Function URL. Same outputs pattern as the
// other share transports: the URL is baked into amplify_outputs.json per branch,
// read at call time so tests can swap it, and absent until that backend is
// deployed.
//
// `shareTasksWriteAvailable()` IS THE FEATURE GATE, and it guards more than a
// button. One backend deploy carries the TaskPogoAssignedTasksShare columns, the
// Lambda and this URL together, so the URL's presence IS "the schema is live" --
// which is what Database.mjs needs before it may name `allowEdits`, `identityId`
// or `notifyEmail` in a document. AppSync rejects a WHOLE operation naming a
// field the branch lacks, and `getAssignedTasksShareForAssignee` runs on every
// open of the Share dialog, so a static document would break Share outright on an
// undeployed branch. See the block comment beside those documents.
//
// ERRORS ARE THROWN, shareUpload.mjs' contract verbatim rather than a second one:
// the body is read even on a failure, because the endpoint answers 403 with "this
// task list is not accepting changes" and 400 with a message that NAMES the field
// it refused -- both things a visitor should be told rather than "something went
// wrong". `error.gone` distinguishes a link revoked (or a task reassigned) while
// the page was open, so the page can say so rather than blaming the change.

import outputs from '../amplify_outputs.json';

function shareTasksWriteBase() {
  return outputs?.custom?.shareTasksWriteFunctionUrl || '';
}

export function shareTasksWriteAvailable() {
  return Boolean(shareTasksWriteBase());
}

async function postAction(payload) {
  const base = shareTasksWriteBase();
  if (!base) throw new Error('Changing a shared task list is not available.');

  const response = await fetch(base, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The honeypot field the endpoint checks, always empty from a real client.
    body: JSON.stringify({ company: '', ...payload }),
  });

  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(body?.error || `That change could not be saved (${response.status}).`);
    error.status = response.status;
    // 410 means the link has been revoked, the task completed by someone else,
    // or the task reassigned away from this list while the page was open.
    error.gone = response.status === 404 || response.status === 410;
    error.rejected = Array.isArray(body?.rejected) ? body.rejected : [];
    throw error;
  }

  return body ?? {};
}

/**
 * Change a task's fields. Resolves to `{ task }` -- the same publicTask shape
 * the read endpoint publishes, re-bucketed, so the page splices it straight into
 * the list rather than refetching.
 */
export async function editSharedTask({ shareId, taskId, fields, editorName }) {
  const body = await postAction({ action: 'edit', shareId, taskId, fields, editorName });
  return { task: body.task ?? null, notified: Boolean(body.notified) };
}

/**
 * Complete it. `{ outcome: 'rolled' | 'deleted', task }` -- `task` is null when
 * the task was a one-off and has been removed.
 */
export async function completeSharedTask({ shareId, taskId, editorName }) {
  const body = await postAction({ action: 'complete', shareId, taskId, editorName });
  return {
    outcome: body.outcome ?? null,
    task: body.task ?? null,
    notified: Boolean(body.notified),
  };
}

/** Remove one photo THE VISITOR ADDED. `key` is the relative `<taskId>/<name>`. */
export async function removeSharedTaskPhoto({ shareId, taskId, key, editorName }) {
  const body = await postAction({ action: 'removePhoto', shareId, taskId, key, editorName });
  return { removed: Boolean(body.removed) };
}

// Reshaped rather than passed through, the read transport's rule: a key the
// Lambda adds and this omits simply never reaches the page.
async function requestUploadSlots({ shareId, taskId, files }) {
  const body = await postAction({
    action: 'presign',
    shareId,
    taskId,
    files: files.map((file) => ({ name: file.name, size: file.size })),
  });
  return {
    uploads: Array.isArray(body.uploads) ? body.uploads : [],
    rejected: Array.isArray(body.rejected) ? body.rejected : [],
  };
}

// One photo, straight to S3.
//
// THE CONTENT TYPE MUST BE THE ONE THE SERVER SIGNED, exactly. It is derived
// from the file's extension server-side and signed into the URL, so sending
// `file.type` instead -- which a browser may spell differently, or leave empty
// -- is a signature mismatch that arrives as a bare 403 and reads like an
// expired URL. Send back what we were given.
async function putPhoto(upload, file) {
  const response = await fetch(upload.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': upload.contentType },
  });
  if (!response.ok) {
    throw new Error(`Could not upload ${file.name} (${response.status}).`);
  }
  return upload.key;
}

/**
 * Add photos to one task: presign, PUT each, then tell the endpoint what landed.
 *
 * Returns `{ photos, notified, rejected, failed }` -- uploadSharedPhotos' shape
 * exactly, so both shared pages report an outcome the same way.
 *
 * PARTIAL ACCEPTANCE, not all-or-nothing: someone adding eight photos on marina
 * wifi must not lose seven to one timeout.
 */
export async function uploadSharedTaskPhotos({
  shareId,
  taskId,
  files,
  editorName,
  onProgress,
}) {
  const sendable = Array.from(files);
  const { uploads, rejected } = await requestUploadSlots({ shareId, taskId, files: sendable });

  const keys = [];
  const failed = [];
  let done = 0;

  // Sequential: several multi-megabyte PUTs at once on a phone's uplink is
  // slower than one at a time and far more likely to time out.
  for (const upload of uploads) {
    // BY INDEX, NOT BY NAME. A phone multi-select routinely yields two files
    // called `IMG_0421.jpg` from different albums, and a Map keyed on the name
    // collapses them. Not a positional zip either: `uploads` is a subsequence of
    // what we sent, because a rejected file is skipped, so the index has to come
    // from the side that did the skipping.
    const file = sendable[upload.index];
    if (!file) continue;
    try {
      keys.push(await putPhoto(upload, file));
    } catch (err) {
      failed.push({ name: upload.name, reason: err.message });
    }
    done += 1;
    if (onProgress) onProgress({ done, total: uploads.length });
  }

  if (keys.length === 0) {
    return { photos: [], notified: false, rejected, failed };
  }

  const body = await postAction({
    action: 'uploaded',
    shareId,
    taskId,
    keys,
    editorName,
  });

  return {
    photos: Array.isArray(body.photos) ? body.photos : [],
    notified: Boolean(body.notified),
    rejected,
    failed,
  };
}
